import { DcmJsDatasetWrapper } from '../dicom/dcmJsWrapper';
import { GenerationModelDetails } from '../dicom/dicomBase';
import { DICOMApprovalStatus } from '../dicom/dicomConstants';
import { DicomStructureSet } from '../dicom/dicomstructureset';
import { DicomRoi } from '../dicom/dicomRoi';
import { Modality } from '../dicom/modality';
import {
    DicomProcessor,
    dcmjsRemoveStructure,
} from '../services/dicomProcessor';
import { Image3D } from './image3d';

/**
 * Patient-level structure set domain model that wraps DicomStructureSet.
 *
 * Provides a higher-level interface for structure set operations, combining
 * DICOM-level functionality with patient-specific concerns like image association.
 * Maintains immutability through the withX() pattern for all state changes.
 */
export class StructureSet implements Iterable<DicomRoi> {
    public frameOfReferenceUid: string;

    public get structureSetLabel(): string {
        return this.dicomStructureSet.structureSetLabel;
    }

    public set structureSetLabel(label: string) {
        this._ensureCanModify();
        this.dicomStructureSet.structureSetLabel = label;
    }

    public get structureSetDescription(): string {
        return this.dicomStructureSet.structureSetDescription;
    }

    public set structureSetDescription(description: string) {
        this._ensureCanModify();
        this.dicomStructureSet.structureSetDescription = description;
    }

    public get approvalStatus(): DICOMApprovalStatus {
        return this.dicomStructureSet.approvalStatus;
    }

    public set approvalStatus(status: DICOMApprovalStatus) {
        this.dicomStructureSet.approvalStatus = status;
    }

    public get isApproved(): boolean {
        return this.approvalStatus === DICOMApprovalStatus.APPROVED;
    }

    public get reviewerName(): string {
        return this.dicomStructureSet.reviewerName;
    }

    public set reviewerName(name: string) {
        this.dicomStructureSet.reviewerName = name;
    }

    public get reviewTime(): string {
        return this.dicomStructureSet.reviewTime;
    }

    public set reviewTime(name: string) {
        this.dicomStructureSet.reviewTime = name;
    }

    public get reviewDate(): string {
        return this.dicomStructureSet.reviewDate;
    }

    public set reviewDate(name: string) {
        this.dicomStructureSet.reviewDate = name;
    }

    public get dicomUid(): string {
        return this.dicomStructureSet.sopInstanceUid;
    }

    public get seriesInstanceUid(): string {
        return this.dicomStructureSet.seriesInstanceUid;
    }

    public get manufacturer(): string {
        return this.dicomStructureSet.manufacturer;
    }

    public get manufacturerModelName(): string {
        return this.dicomStructureSet.manufacturerModelName;
    }

    public get softwareVersions(): string {
        return this.dicomStructureSet.softwareVersions;
    }

    /**
     * Map where key is the {@link DicomRoi.roiNumber} and value is the {@link DicomRoi}.
     */
    public get rois(): ReadonlyMap<number, DicomRoi> {
        return this.dicomStructureSet.rois;
    }

    public get roiArray(): DicomRoi[] {
        return Array.from(this.dicomStructureSet.rois.values());
    }

    public get image(): Image3D | null {
        return this._image;
    }

    public set image(image: Image3D | null) {
        this._image = image;
    }

    public get numberOfStructures(): number {
        return this.dicomStructureSet.numberOfStructures;
    }

    /**
     * Get an ROI by its roiNumber.
     * @param roiNumber - The number of the ROI you want to retrieve.
     * @returns The corresponding ROI, or undefined if not found.
     */
    public getRoiByNumber(roiNumber: number): DicomRoi | undefined {
        return this.dicomStructureSet.rois.get(roiNumber);
    }

    /**
     * Sets approval status with cascading (mutable version).
     * @param approvalStatus The new approval status
     * @param reviewerName The reviewer name
     * @param cascade If true, also updates all ROI approval statuses to match
     */
    public setApprovalStatus(
        approvalStatus: DICOMApprovalStatus,
        reviewerName: string,
        cascade: boolean = true
    ): void {
        this.dicomStructureSet.setApprovalStatus(approvalStatus, reviewerName);
        if (cascade) {
            this.dicomStructureSet.rois.forEach((roi) => {
                roi.approvalStatus = approvalStatus;
            });
        }
    }

    /**
     * Get all ROIs with a specific approval status.
     * @param approvalStatus - The approval status to filter by.
     * @returns An array of ROIs with the specified approval status.
     *
     */
    public getRoiByApprovalStatus(
        approvalStatus: DICOMApprovalStatus
    ): DicomRoi[] {
        return this.roiArray.filter(
            (roi) => roi.approvalStatus === approvalStatus
        );
    }

    /**
     * Determines if the structure set approval status should cascade based on ROI states.
     *
     * @returns The cascaded approval status, or null if no change needed:
     * - APPROVED if all ROIs are approved
     * - REJECTED if all ROIs are rejected
     * - UNAPPROVED if ROIs have mixed states
     * - null if current status already matches the calculated status
     */
    public getCascadingApprovalStatus(): DICOMApprovalStatus | null {
        if (this.numberOfStructures === 0) {
            return null;
        }

        const approvedCount = this.getRoiByApprovalStatus(
            DICOMApprovalStatus.APPROVED
        ).length;
        const rejectedCount = this.getRoiByApprovalStatus(
            DICOMApprovalStatus.REJECTED
        ).length;

        let targetStatus: DICOMApprovalStatus;

        if (approvedCount === this.numberOfStructures) {
            targetStatus = DICOMApprovalStatus.APPROVED;
        } else if (rejectedCount === this.numberOfStructures) {
            targetStatus = DICOMApprovalStatus.REJECTED;
        } else {
            targetStatus = DICOMApprovalStatus.UNAPPROVED;
        }

        return this.approvalStatus === targetStatus ? null : targetStatus;
    }

    public exportWith(roiNumbers: number[], characterSet?: string): Blob {
        const dicomClone = new DcmJsDatasetWrapper(
            DicomProcessor.cloneDicomDataset(this.dicomStructureSet)
        );
        if (characterSet) {
            dicomClone.setCharacterSet(characterSet);
        }
        const allRois = this.dicomStructureSet.rois.keys();
        for (const roiNumber of allRois) {
            if (!roiNumbers.includes(roiNumber)) {
                dcmjsRemoveStructure(dicomClone, roiNumber);
            }
        }
        return dicomClone.asBlob();
    }

    public get generationModel(): GenerationModelDetails | undefined {
        return this.dicomStructureSet.generationModel;
    }

    public get isNew(): boolean {
        return this.dicomStructureSet.isNew;
    }

    public get isExported(): boolean {
        return this.dicomStructureSet.isExported;
    }

    public setExported(): void {
        this.dicomStructureSet.setExported();
    }

    public export(): Blob {
        return this.dicomStructureSet.datasetWrapper.asBlob();
    }
    /**
     * Exports the structure set as a Monaco DIR file.
     * This is a custom format used for sharing structure sets in a specific way.
     * @param patientName - The name of the patient, used in the export.
     * @returns A Blob representing the exported structure set in Monaco format.
     */
    public exportAsMonacoFile(patientName: string): Blob {
        if (!this.image) {
            throw new Error(
                'Cannot export Monaco file without an associated image.'
            );
        }

        const modality = this.image.seriesMetadata.modality;

        if (modality !== Modality.CT && modality !== Modality.MR) {
            throw new Error(
                'Can only export Monaco file for CT or MR modalities.'
            );
        }
        const formatOrientation = (num: number): string => {
            if (num === 0) {
                return '0';
            }
            if (num === 1) {
                return '1';
            }
            if (num === -1) {
                return '-1';
            }
            return num.toFixed(6);
        };

        const imagePositionLines = Array.from(
            this.image.firstSlice.imagePosition
        ).map((x) => x.toFixed(4));
        // export only the X and Y direction cosines as defined in DICOM
        const imageOrientationLines = Array.from(
            this.image.firstSlice.imageOrientation.slice(0, 6) // Only take the first 6 elements for orientation
        ).map(formatOrientation);

        const monacoDirFileLines: (string | number)[] = [
            '!Version=2',
            modality,
            this.image.sopClassUid,
            this.image.seriesMetadata.seriesInstanceUID,
            this.image.firstSlice.studyInstanceUID,
            'NO_ID',
            patientName,
            this.image.frameOfReferenceUid,
            ...imagePositionLines,
            ...imageOrientationLines,
            'RTSTRUCT',
            '1.2.840.10008.5.1.4.1.1.481.3', // DICOMClassSopUIDs.rt_struct.value
            this.dicomStructureSet.sopInstanceUid,
            this.dicomStructureSet.seriesInstanceUid,
            'NO_ID',
            this.structureSetDescription || 'NO_DESCRIPTION',
            '',
            '',
            '',
            '',
            0,
            0,
            0,
            0,
            0,
            0,
        ];

        const monacoDirFileContent = monacoDirFileLines.join('\n') + '\n';

        return new Blob([monacoDirFileContent], {
            type: 'text/plain;charset=utf-8',
        });
    }

    private _ensureCanModify() {
        if (this.isApproved) {
            throw new Error(
                `Cannot modify approved structure set '${this.structureSetLabel}'`
            );
        }
    }

    constructor(
        public readonly dicomStructureSet: DicomStructureSet,
        frameOfReferenceUid: string,
        private _image: Image3D | null
    ) {
        this.frameOfReferenceUid = frameOfReferenceUid;
    }

    public [Symbol.iterator](): Iterator<DicomRoi> {
        return this.dicomStructureSet[Symbol.iterator]();
    }
}
